import React, { useCallback, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { HideDoneItemsToggle$key } from '@atlassian/jira-relay/src/__generated__/HideDoneItemsToggle.graphql';
import { useQueryParam } from '@atlassian/react-resource-router';
import { useUpdateHideDoneMutation } from '../services/update-hide-done-toggle/useUpdateHideDoneMutation.tsx';
import messages from './messages.tsx';

export type HideDoneToggleProps = {
	isSavedViewsEnabled: boolean;
	viewResult: HideDoneItemsToggle$key;
	onChangeHideDone: (isHideDoneEnabled: boolean) => void;
	onResetSelectedIssues?: () => void;
};

export const HideDoneItemsToggle = ({
	isSavedViewsEnabled,
	viewResult,
	onChangeHideDone,
	onResetSelectedIssues,
}: HideDoneToggleProps) => {
	const viewResultData = useFragment<HideDoneItemsToggle$key>(
		graphql`
			fragment HideDoneItemsToggle on JiraIssueSearchViewMetadata
			@argumentDefinitions(
				staticViewInput: { type: "JiraIssueSearchStaticViewInput" }
				isSavedViewsEnabled: { type: "Boolean!" }
			) {
				__id
				id
				viewId
				savedViewId @include(if: $isSavedViewsEnabled)
				... on JiraSpreadsheetView {
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
						settings: $settings
					) {
						hideDone
					}
				}
			}
		`,
		viewResult,
	);

	const { viewSettings, id, viewId, __id: issueSearchViewId, savedViewId } = viewResultData;
	const { hideDone } = viewSettings ?? {};

	const [hideDoneIsChecked, setHideDoneIsChecked] = useState(hideDone);

	const [, setHideDoneParam] = useQueryParam('hideDone');
	const [updateHideDone, updateHideDoneOld] = useUpdateHideDoneMutation();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onChange = useCallback(() => {
		if (id) {
			fireUIAnalytics(createAnalyticsEvent({}), 'toggle clicked', 'hideDoneItems', {
				hideDone: !hideDone,
			});

			setHideDoneIsChecked(!hideDone);
			if (fg('jsc_list_query_param_merge')) {
				setHideDoneParam(hideDone ? 'false' : 'true');
			}

			if (isSavedViewsEnabled) {
				if (savedViewId == null) {
					throw new Error('Missing saved view ID');
				}

				updateHideDone({
					isHideDoneEnabled: !hideDone,
					issueSearchViewId,
					onChangeHideDone,
					viewARI: savedViewId,
				});
			} else {
				updateHideDoneOld({
					isHideDoneEnabled: !hideDone,
					issueSearchViewId,
					issueSearchViewResultAri: id,
					onChangeHideDone,
				});
			}

			// reset the selected issue for the bulk operations when chaging the toggle
			onResetSelectedIssues && onResetSelectedIssues();
		}
	}, [
		createAnalyticsEvent,
		hideDone,
		id,
		issueSearchViewId,
		onChangeHideDone,
		onResetSelectedIssues,
		savedViewId,
		setHideDoneParam,
		updateHideDone,
		updateHideDoneOld,
		isSavedViewsEnabled,
	]);

	const toggleId = `${viewId}_hide_done_toggle`;
	const { formatMessage } = useIntl();

	return (
		<Stack>
			<Inline spread="space-between" alignBlock="center">
				<label htmlFor={toggleId}>
					{formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? messages.showHideDoneItems
							: messages.showHideDoneItemsOldTerminology,
					)}
				</label>
				<Toggle
					size={fg('jsc-1730-adjust_toggle_size') ? undefined : 'large'}
					id={toggleId}
					onChange={onChange}
					isChecked={Boolean(hideDoneIsChecked)}
				/>
			</Inline>
		</Stack>
	);
};
